/**
 * Which login mechanism the console uses.
 *
 *  - `coherence` (DEFAULT): Formant Coherence / `@formant/auth-lib`. A bare URL
 *    load lands here, so Metaphysics defaults to the Formant/Robotics login and
 *    the user's real telemetry org.
 *  - `keycloak` (explicit opt-in only): the cortex-Keycloak OIDC flow, reached
 *    by the dedicated `/keycloak` path or `?login=keycloak`.
 *
 * An inbound `?kc_idp_hint` NO LONGER selects Keycloak. It used to (for the
 * app-switcher / cross-app SSO-linking), but that routed users through the
 * Keycloak broker, which JIT-creates a *duplicate* org + user distinct from
 * their telemetry identity. The app switcher now links to the plain `/` URL
 * (see `@formant/ui-kit` `resolveAppHref`); a stray hint from a stale link is
 * ignored here so it can't reintroduce the duplicate-org login. The hint is
 * still honored for IdP *forwarding* once Keycloak mode is chosen via
 * `/keycloak` — `/keycloak?kc_idp_hint=oauth2` still brokers Robotics SSO.
 *
 * The choice is sticky within a tab (it must survive the login redirect
 * round-trip, which returns to `/` without the marker) via `sessionStorage`,
 * and is cleared on sign-out so the next sign-in falls back to the default.
 */
export type AuthMode = 'coherence' | 'keycloak';

const storageKey = 'cortex.authMode';

/** Dedicated path that opts a load into the Coherence (Formant) login flow. */
export const coherenceLoginPath = '/coherence';

/** Dedicated path that opts a load into the cortex-Keycloak login flow (the
 *  path equivalent of `?login=keycloak`). */
export const keycloakLoginPath = '/keycloak';

/**
 * Resolve the auth mode from the current URL (path + search) and sessionStorage.
 *
 * Precedence: an explicit `?login=keycloak` / `/keycloak` opt-in → an explicit
 * `?login=coherence` / `/coherence` opt-in → the sticky per-tab choice → the
 * default (`coherence`). A bare `?kc_idp_hint` is NOT a mode signal (see the
 * module doc). Any explicit choice is persisted so it survives the redirect
 * round-trip. Returns the mode plus the cleaned search string (the caller
 * applies it via `history.replaceState`, and also strips the `/keycloak` /
 * `/coherence` marker path). The `login` param is always removed; `kc_idp_hint`
 * is kept only when Keycloak mode is chosen (keycloak-auth forwards it to
 * pre-select the IdP) and stripped otherwise so a stale hint can't linger.
 */
export function resolveAuthMode(
  search: string,
  pathname: string,
): { mode: AuthMode; cleanedSearch: string } {
  const params = new URLSearchParams(search);
  const login = params.get('login');
  const cleaned = (dropIdpHint: boolean): string => {
    params.delete('login');
    if (dropIdpHint) params.delete('kc_idp_hint');
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  // Explicit Keycloak opt-in — the dedicated `/keycloak` path or `?login=keycloak`.
  // This is now the ONLY route into Keycloak; a bare `?kc_idp_hint` no longer
  // qualifies. The hint is KEPT on the search here so keycloak-auth /
  // oidc.startSignin can still forward it to pre-select the Robotics SSO IdP
  // (`/keycloak?kc_idp_hint=oauth2`). Persisted so it survives the round-trip.
  if (login === 'keycloak' || pathname === keycloakLoginPath) {
    sessionStorage.setItem(storageKey, 'keycloak');
    return { mode: 'keycloak', cleanedSearch: cleaned(false) };
  }
  if (login === 'coherence' || pathname === coherenceLoginPath) {
    sessionStorage.setItem(storageKey, 'coherence');
    return { mode: 'coherence', cleanedSearch: cleaned(true) };
  }

  // Sticky per-tab choice, else the default (coherence). A stray `?kc_idp_hint`
  // (e.g. a stale app-switcher/bookmark link from before the Coherence-default
  // flip) is deliberately ignored as a mode signal — it used to force the
  // Keycloak-broker login into a duplicate org. Drop it from a Coherence result
  // so it can't linger through the Coherence login round-trip; keep it when the
  // sticky choice is Keycloak (forwarding still wants it).
  const mode: AuthMode =
    sessionStorage.getItem(storageKey) === 'keycloak' ? 'keycloak' : 'coherence';
  return { mode, cleanedSearch: cleaned(mode === 'coherence') };
}

/**
 * The sticky auth mode for this tab (default `coherence`). Read after bootstrap
 * to branch UI that only applies to one provider — e.g. hiding Keycloak-only
 * admin surfaces (Users, Organizations) when signed in via Coherence, where
 * user/org management lives in the Formant platform, not this console.
 */
export function getAuthMode(): AuthMode {
  return sessionStorage.getItem(storageKey) === 'keycloak' ? 'keycloak' : 'coherence';
}

/** Forget a persisted choice (called on sign-out) → next sign-in uses the default. */
export function clearAuthMode(): void {
  sessionStorage.removeItem(storageKey);
}
